import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Tabs, Modal, theme } from "antd";
import { PhysioReviewContext } from "../../../Physiotherapist/PhysiotherapistReview/PhysioReviewFormProvider";
import { PHYSIO_SAVE_CLOSE, PHYSIO_SAVE_CONTINUE } from "./usePhysioScaleExternalSave";
import "./PhysioScales.css";

const { TabPane } = Tabs;
const GCScale = lazy(() => import("./GCScale"));
const NIHSScale = lazy(() => import("./NIHSScale"));
const MOCAScale = lazy(() => import("./MOCAScale"));
const RASScale = lazy(() => import("./RASScale"));
const ASIAScale = lazy(() => import("./ASIAScale"));
const VAScale = lazy(() => import("./VAScale"));
const MAScale = lazy(() => import("./MAScale"));
const MMTScale = lazy(() => import("./MMTScale"));
const ROMScale = lazy(() => import("./ROMScale"));
const VCGScale = lazy(() => import("./VCGScale"));
const RPEScale = lazy(() => import("./RPEScale"));
const Reflexes = lazy(() => import("./Reflexes"));
const PeakFlowMeterTest = lazy(() => import("./PeakFlowMeterTest"));
const SixMinuteWalkTest = lazy(() => import("./SixMinuteWalkTest"));
const Coordination = lazy(() => import("./Coordination"));
const TCMScale = lazy(() => import("./TCMScale"));
const FingerGripTest = lazy(() => import("./FingerGripTest"));
const BarthelIndex = lazy(() => import("./BarthelIndex"));

const PhysioScales = ({
  admission,
  onScalesSubmit,
  scalesId,
  setScalesId,
  scaleTab,
  setScaleTab,
  setActiveTab,
  setVisible,
  onClose,
  patientData,
  isScaleDirty,
  setIsScaleDirty,
  triggerScaleSave,
  setTriggerScaleSave,
  nextMainTab,
  setNextMainTab,
  scaleNavRef,
}) => {
  const { token } = theme.useToken();
  const [triggerSave, setTriggerSave] = useState(false);
  const [nextTab, setNextTab] = useState(null);
  const [savePulse, setSavePulse] = useState(0);
  const pendingTabRef = useRef(null);
  const scaleTabRef = useRef(scaleTab);
  const navigatingRef = useRef(false);
  const saveActionRef = useRef(PHYSIO_SAVE_CONTINUE);

  useEffect(() => {
    scaleTabRef.current = scaleTab;
  }, [scaleTab]);

  const handleSetVisible = (value) => {
    if (value === false) {
      onClose?.();
      return;
    }
    setVisible?.(value);
  };

  const switchToTab = useCallback(
    (key) => {
      const target = String(key);
      pendingTabRef.current = null;
      setNextTab(null);
      setIsScaleDirty?.(false);
      setScaleTab(target);
    },
    [setIsScaleDirty, setScaleTab]
  );

  const pulseSave = useCallback((destinationKey) => {
    const target = destinationKey != null ? String(destinationKey) : null;
    pendingTabRef.current = target;
    setNextTab(target);
    // Pulse so child useEffects always re-fire even if triggerSave was already true
    setTriggerSave(false);
    setSavePulse((n) => n + 1);
  }, []);

  useEffect(() => {
    if (savePulse === 0) return;
    setTriggerSave(true);
  }, [savePulse]);

  useEffect(() => {
    if (triggerScaleSave) {
      if (pendingTabRef.current == null && nextTab == null) {
        setNextTab(scaleTabRef.current);
      }
      pulseSave(pendingTabRef.current ?? nextTab ?? scaleTabRef.current);
    } else {
      setTriggerSave(false);
    }
    // Only react to parent save toggles, not every nextTab/scaleTab change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerScaleSave]);

  useEffect(() => {
    setIsScaleDirty?.(false);
  }, [scaleTab, setIsScaleDirty]);

  const LAST_SCALE_KEY = "18";
  const isLastScale = scaleTab === LAST_SCALE_KEY;

  const handleScaleSaveAndClose = () => {
    saveActionRef.current = PHYSIO_SAVE_CLOSE;
    pendingTabRef.current = null;
    setNextTab(null);
    pulseSave(null);
  };

  const handleScaleSaveAndProceed = () => {
    saveActionRef.current = PHYSIO_SAVE_CONTINUE;
    if (isLastScale) {
      if (isScaleDirty) {
        pendingTabRef.current = null;
        setNextTab(null);
        pulseSave(null);
      } else {
        setActiveTab?.("3");
      }
      return;
    }

    const nextKey = String(Number(scaleTab) + 1);
    if (isScaleDirty) {
      pulseSave(nextKey);
    } else {
      switchToTab(nextKey);
    }
  };

  useEffect(() => {
    if (!scaleNavRef) return;
    scaleNavRef.current = {
      saveClose: handleScaleSaveAndClose,
      saveProceed: handleScaleSaveAndProceed,
      isLastScale,
    };
  });

  const handleScaleTabChange = (key) => {
    const target = String(key);
    if (!target || target === String(scaleTabRef.current)) return;
    // Prevent double-fire from onTabClick + onChange on some mobile browsers
    if (navigatingRef.current) return;
    navigatingRef.current = true;
    window.setTimeout(() => {
      navigatingRef.current = false;
    }, 300);

    if (!isScaleDirty) {
      switchToTab(target);
      return;
    }

    Modal.confirm({
      title: "Unsaved changes",
      content: <div style={{ marginTop: 12 }}>Save and proceed?</div>,
      okText: "Save",
      cancelText: "Discard",
      centered: true,
      okButtonProps: {
        style: {
          background: token.colorPrimary,
          borderColor: token.colorPrimary,
          color: "#fff",
          fontWeight: 500,
        },
      },
      cancelButtonProps: {
        style: {
          height: 34,
          padding: "0 18px",
        },
      },
      onOk: () => {
        pulseSave(target);
      },
      onCancel: () => {
        switchToTab(target);
      },
    });
  };

  return (
    <div className="physio-scales no-scroll physio-scales-with-nav-footer">
      <Tabs
        activeKey={String(scaleTab)}
        type="card"
        tabPosition="top"
        animated={false}
        destroyInactiveTabPane
        // onTabClick is more reliable on mobile scrollable tab bars than onChange alone
        onTabClick={(key) => handleScaleTabChange(key)}
        onChange={(key) => handleScaleTabChange(key)}
      >
        <TabPane tab="GCS" key="1">
          <Suspense fallback={<div>Loading GCS...</div>}>
            <GCScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              scalesId={scalesId}
              setScalesId={setScalesId}
              scaleTab={scaleTab}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "1"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="NIHSS" key="2">
          <Suspense fallback={<div>Loading NIHSS...</div>}>
            <NIHSScale
              onScalesSubmit={onScalesSubmit}
              admission={admission}
              setActiveTab={setActiveTab}
              scalesId={scalesId}
              setScalesId={setScalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "2"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="MOCA" key="3">
          <Suspense fallback={<div>Loading MOCA...</div>}>
            <MOCAScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "3"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="RASS" key="4">
          <Suspense fallback={<div>Loading RAS...</div>}>
            <RASScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              scalesId={scalesId}
              setScalesId={setScalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "4"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="ASIA" key="5">
          <Suspense fallback={<div>Loading ASIA...</div>}>
            <ASIAScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "5"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="VAS" key="6">
          <Suspense fallback={<div>Loading VAS...</div>}>
            <VAScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "6"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="MAS" key="7">
          <Suspense fallback={<div>Loading MAS...</div>}>
            <MAScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "7"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="MMT" key="8">
          <Suspense fallback={<div>Loading MMT...</div>}>
            <MMTScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "8"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="ROM" key="9">
          <Suspense fallback={<div>Loading ROM...</div>}>
            <ROMScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "9"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="VCG" key="10">
          <Suspense fallback={<div>Loading VCG...</div>}>
            <VCGScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "10"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="RPE" key="11">
          <Suspense fallback={<div>Loading RPE...</div>}>
            <RPEScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "11"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="Reflexes" key="12">
          <Suspense fallback={<div>Loading Reflexes...</div>}>
            <Reflexes
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "12"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="Peak Flow Meter Test" key="13">
          <Suspense fallback={<div>Loading Peak Flow Meter Test...</div>}>
            <PeakFlowMeterTest
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              patientData={patientData}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "13"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="Six Minute Walk Test" key="14">
          <Suspense fallback={<div>Loading Six Minute Walk Test...</div>}>
            <SixMinuteWalkTest
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              patientData={patientData}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "14"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="Coordination" key="15">
          <Suspense fallback={<div>Loading Coordination...</div>}>
            <Coordination
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "15"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="TCM" key="16">
          <Suspense fallback={<div>Loading TCM...</div>}>
            <TCMScale
              context={PhysioReviewContext}
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              triggerSave={triggerSave && scaleTab === "16"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="Finger Grip Test" key="17">
          <Suspense fallback={<div>Loading Finger Grip Test...</div>}>
            <FingerGripTest
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              patientData={patientData}
              triggerSave={triggerSave && scaleTab === "17"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
        <TabPane tab="Barthel Index" key="18">
          <Suspense fallback={<div>Loading Barthel Index...</div>}>
            <BarthelIndex
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={handleSetVisible}
              onClose={onClose}
              setIsScaleDirty={setIsScaleDirty}
              isLastScale
              triggerSave={triggerSave && scaleTab === "18"}
              setTriggerSave={setTriggerSave}
              saveActionRef={saveActionRef}
              nextTab={nextTab}
              setNextTab={setNextTab}
              hideFooterInNeuro={true}
            />
          </Suspense>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default PhysioScales;
